/* global axios */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import api from '../services/apiService';
import { formatDateDE } from '../utils/dateFormatter';

function OpenPaymentReport() {
  // Filter
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  
  const [includePayments] = useState(false);

  // Paging + Daten
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [sumGross, setSumGross] = useState(0);
  const [sumOpen, setSumOpen] = useState(0);

  // UI
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Kundensuche
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // expand + lazy payments
  const [expanded, setExpanded] = useState({});
  const [invoicesByCustomerId, setInvoicesByCustomerId] = useState({});
  const [rowInvoiceLoading, setRowInvoiceLoading] = useState({});

  const toggleRow = async (row) => {
   const id = row.id; // = customerId aus der Hauptliste
    const willOpen = !expanded[id];
    setExpanded(prev => ({ ...prev, [id]: willOpen }));

    if (!willOpen) return;
    
   if (Array.isArray(row.invoices) && row.invoices.length) return; // server hat evtl. schon geliefert
   if (Array.isArray(invoicesByCustomerId[id]) && invoicesByCustomerId[id].length) return;

    try {
      setRowInvoiceLoading(prev => ({ ...prev, [id]: true }));
     const { data } = await api.get(`/reports/payments/open/by-customer/${id}`, {
       params: { dateFrom, dateTo, createdFrom, createdTo, search, includePayments },
     });
     setInvoicesByCustomerId(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      console.error(`Rechnungen für Kunde ${id} konnten nicht geladen werden:`, e);
     setInvoicesByCustomerId(prev => ({ ...prev, [id]: [] }));
    } finally {
      setRowInvoiceLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // helpers
  const buildParams = useCallback(() => {
    const params = { page, pageSize };
    if (search) params.search = search;
    if (customerId) params.customerId = customerId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (createdFrom) params.createdFrom = createdFrom;
    if (createdTo) params.createdTo = createdTo;
    
    return params;
  }, [page, pageSize, search, customerId, dateFrom, dateTo, createdFrom, createdTo, ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const params = buildParams();
      const { data } = await api.get('/reports/payments/open', { params });
      setRows(data.data || []);
      setTotal(Number(data.total || 0));
      setPages(Math.max(1, Number(data.pages ?? 1)));
      setSumGross(Number(data.sumGross || 0));
      setSumOpen(Number(data.sumOpen || 0));
    } catch (e) {
      console.error('OpenPaymentReport laden fehlgeschlagen:', e);
      setErrMsg('Fehler beim Laden des Berichts');
      setRows([]); setTotal(0); setPages(0); setSumGross(0); setSumOpen(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Kundenliste (debounced) – analog OrdersEntryReport
  const loadCustomers = useMemo(() => debounce(async (q) => {
    try {
      const { data } = await api.get('/customers', { params: { search: q || '', limit: 10 } });
      const opts = (Array.isArray(data) ? data : []).map(c => ({
        value: c.id,
        label: `${c.company} (${c.customerNumber || '—'})`,
        isBlocked: !!c.isBlocked,
        blockedUntil: c.blockedUntil || null,
        blockReason: c.blockReason || null,
      }));
      setCustomerOptions(opts);
    } catch (e) {
      console.error('Kunden laden fehlgeschlagen:', e);
      setCustomerOptions([]);
    }
  }, 300), []);

  useEffect(() => {
    loadCustomers('');
    return () => loadCustomers.cancel();
  }, [loadCustomers]);

  const handleCustomerInputChange = (inputValue, { action }) => {
    if (action === 'input-change') loadCustomers(inputValue);
  };

  const handleCustomerChange = (opt) => {
    setSelectedCustomer(opt || null);
    setCustomerId(opt ? String(opt.value) : '');
    setPage(1);
  };

  const customerOptionStyles = {
    option: (base, { data, isFocused, isSelected }) => ({
      ...base,
      color: data.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data.isBlocked ? 600 : 400,
      backgroundColor: isFocused
        ? (data.isBlocked ? '#fee2e2' : base.backgroundColor)
        : isSelected ? base.backgroundColor : 'transparent',
    }),
    singleValue: (base, { data }) => ({
      ...base,
      color: data?.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data?.isBlocked ? 600 : 400,
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (page !== 1) setPage(1);
    else await fetchData();
  };

  // CSV
  const handleDownloadCSV = async () => {
    try {
      const params = { ...buildParams(), format: 'csv' };
      const response = await api.get('/reports/payments/open', { params, responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || 'open_payment_report_customers.csv';
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV-Download fehlgeschlagen:', e);
      setErrMsg('CSV-Download fehlgeschlagen');
    }
  };

  const gotoPrev = () => setPage(p => Math.max(1, p - 1));
  const gotoNext = () => setPage(p => Math.min(pages || 1, p + 1));

// Initial + Paging (identisch zu OrdersEntryReport)
useEffect(() => { fetchData(); }, [fetchData]);
useEffect(() => { fetchData(); }, [page, pageSize, fetchData]);

// Auto-Reset der Seite bei Filteränderungen (ohne sofortiges fetchData)
useEffect(() => {
  setPage(1);
}, [customerId, search, dateFrom, dateTo, createdFrom, createdTo, includePayments]);



  const money = (v) => (Number(v || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2 }) + ' €';
  const fmtDate = (d) => formatDateDE(d, '—');

  return (
    <div style={{ maxWidth: 1100, margin: 'auto' }}>
      <Helmet>
        <title>Bericht: Offene Posten</title>
        <meta name="Open Payment Report" content="Report offene Rechnungen/Zahlungen" />
      </Helmet>

      <h2>📊 Offene Posten</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          {/* Kunde */}
          <div style={{ flex: '1 1 260px', minWidth: 260 }}>
            <label htmlFor="customerSelect" style={{ display: 'inline-block', marginBottom: 4 }}>
              Kunde
            </label>
            <Select
              inputId="customerSelect"
              className="select-box-cst"
              isClearable
              placeholder="Kunde suchen…"
              options={customerOptions}
              value={selectedCustomer}
              onInputChange={handleCustomerInputChange}
              onChange={handleCustomerChange}
              getOptionValue={(o) => String(o.value)}
              styles={{
                ...customerOptionStyles,
                container: (base) => ({ ...base, marginTop: -5 }),
                control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
              }}
            />
          </div>

          {/* Rechnungsdatum ab/bis */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Rechnungsdatum ab</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Rechnungsdatum bis</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Erstellt ab/bis */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Erstellt ab</label>
            <input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Erstellt bis</label>
            <input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Suche */}
          <div style={{ flex: '1 1 180px', minWidth: 180 }}>
            <label>Suche</label>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rech.-Nr., Kunde…"
              style={{ width: '100%' }}
            />
          </div>

          {/* Rechts: Checkbox + CSV */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto', }}>
            <button type="button" onClick={handleDownloadCSV} disabled={loading} style={{ marginBottom: '12px' }}>CSV herunterladen</button>
          </div>
        </div>
      </form>

      {errMsg && <p style={{ color: 'red' }}>{errMsg}</p>}

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <strong>Gesamt:</strong> {total} Kunden &nbsp;|&nbsp;
          <strong> Summe Brutto:</strong> {money(sumGross)} &nbsp;|&nbsp;
          <strong> Summe offen:</strong> {money(sumOpen)}
        </div>
        <div>
          <button onClick={gotoPrev} disabled={loading || page <= 1}>Zurück</button>
          <span style={{ margin: '0 8px' }}>Seite {page} / {pages || 1}</span>
          <button onClick={gotoNext} disabled={loading || page >= (pages || 1)}>Weiter</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fffefeff' }}>
              <th style={{ width: 34 }}></th>
              <th style={{ textAlign: 'left', padding: 8 }}>Kunde</th>
     <th style={{ textAlign: 'right', padding: 8 }}>Rechnungen offen (#)</th>
     <th style={{ textAlign: 'right', padding: 8 }}>Summe Brutto (€)</th>
     <th style={{ textAlign: 'right', padding: 8 }}>Summe offen (€)</th>
     <th style={{ textAlign: 'left', padding: 8 }}>Letzte Zahlung</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan="6" style={{ padding: 12, textAlign: 'center' }}>Keine Daten</td></tr>
            )}

            {rows.map(r => {
              const responseInvoices = Array.isArray(r.invoices) ? r.invoices : null;
   const cachedInvoices = invoicesByCustomerId[r.id];
   const visibleInvoices = responseInvoices && responseInvoices.length ? responseInvoices : (cachedInvoices || []);
              const isOpen = !!expanded[r.id];

              return (
                <React.Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <td style={{ padding: 0, textAlign: 'center' }}>
                       <button
  type="button"
  onClick={() => toggleRow(r)}
  title={isOpen ? 'Offene Rechnungen verbergen' : 'Offene Rechnungen anzeigen'}
  style={{
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 4,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 16,
  }}
>
  <span style={{ fontSize: 16 }}>📂</span>
</button>

                    </td>

                    
                    <td style={{ padding: 8 }}>{r.customer?.company || '—'}</td>
                   <td style={{ padding: 8, textAlign: 'right' }}>{Number(r.invoiceCount || 0)}</td>
         <td style={{ padding: 8, textAlign: 'right' }}>{money(r.sumGross)}</td>
         <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{money(r.sumOpen)}</td>
         <td style={{ padding: 8 }}>{fmtDate(r.lastPaymentDate)}</td>
                  </tr>

                  {isOpen && (
         <tr>
           <td colSpan={6} style={{ background: '#ffffffff', padding: 8 }}>
             {rowInvoiceLoading[r.id] ? (
               <div style={{ padding: 6 }}>Offene Rechnungen werden geladen…</div>
             ) : visibleInvoices.length === 0 ? (
               <div style={{ padding: 6 }}>Keine offenen Rechnungen vorhanden.</div>
             ) : (
               <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: 6 }}>Rech.-Nr.</th>
                     <th style={{ textAlign: 'left', padding: 6 }}>Datum</th>
                     <th style={{ textAlign: 'right', padding: 6 }}>Brutto</th>
                     <th style={{ textAlign: 'right', padding: 6 }}>Bezahlt</th>
                     <th style={{ textAlign: 'right', padding: 6 }}>Offen</th>
                     <th style={{ textAlign: 'left', padding: 6 }}>Letzte Zahlung</th>
                     <th style={{ textAlign: 'left', padding: 6 }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleInvoices.map(inv => (
                     <React.Fragment key={inv.id}>
                       <tr style={{ borderTop: '1px solid #ffffffff' }}>
                         <td style={{ padding: 6 }}>{inv.invoiceNumber || '—'}</td>
                         <td style={{ padding: 6 }}>{fmtDate(inv.date)}</td>
                         <td style={{ padding: 6, textAlign: 'right' }}>{money(inv.totalGross)}</td>
                         <td style={{ padding: 6, textAlign: 'right' }}>{money(inv.amountPaid)}</td>
                         <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{money(inv.openAmount)}</td>
                         <td style={{ padding: 6 }}>{fmtDate(inv.lastPaymentDate)}</td>
                         <td style={{ padding: 6 }}>
                           {inv.paymentState === 'paid' ? 'Bezahlt' : inv.paymentState === 'partial' ? 'Teilzahlung' : 'Unbezahlt'}
                         </td>
                       </tr>

                       {includePayments && Array.isArray(inv.payments) && inv.payments.length > 0 && (
                         <tr>
                           <td colSpan={7} style={{ background: '#fafafa', padding: 0 }}>
                             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                               <thead>
                                 <tr>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Zahlungs-ID</th>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Datum</th>
                                   <th style={{ textAlign: 'right', padding: 6 }}>Betrag</th>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Währung</th>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Methode</th>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Status</th>
                                   <th style={{ textAlign: 'left', padding: 6 }}>Referenz</th>
                                 </tr>
                               </thead>
                               <tbody>
                                 {inv.payments.map(p => (
                                   <tr key={p.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                     <td style={{ padding: 6 }}>{p.id}</td>
                                     <td style={{ padding: 6 }}>{fmtDate(p.paymentDate)}</td>
                                     <td style={{ padding: 6, textAlign: 'right' }}>{money(p.amount)}</td>
                                     <td style={{ padding: 6 }}>{p.currency || 'EUR'}</td>
                                     <td style={{ padding: 6 }}>{p.method || '-'}</td>
                                     <td style={{ padding: 6 }}>{p.status || '-'}</td>
                                     <td style={{ padding: 6 }}>{p.reference || '-'}</td>
                                   </tr>
                                 ))}
                               </tbody>
                             </table>
                           </td>
                         </tr>
                       )}
                     </React.Fragment>
                   ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && <p style={{ marginTop: 8 }}>Lade…</p>}
    </div>
  );
}

export default OpenPaymentReport;
